import { useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Card, Descriptions, Tag, Button, Breadcrumb } from 'antd'
import { EditOutlined, UserOutlined } from '@ant-design/icons'
import { usersApi } from '@/api/users'
import { PermissionManager } from '@/components/permissions'
import { UserFeatures } from '@/components/users/UserFeatures'
import { Auth0Integration } from '@/components/users/Auth0Integration'
import { UserAuditLog } from '@/components/users/UserAuditLog'
import { usePageHeader } from '@/hooks/usePageHeader'
import { PageLoading } from '@/components/PageLoading'

export const UserDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersApi.getUser(id!),
    enabled: !!id,
  })

  // Get user data (may be undefined during loading)
  const user = data?.data

  // Handle edit navigation
  const handleEdit = useCallback(() => {
    if (user) {
      navigate(`/users/${id}/edit`)
    }
  }, [user, id, navigate])

  // Memoize page header config - hooks must be at top level, before any returns
  const pageHeaderConfig = useMemo(() => {
    if (!user) {
      return {} // Empty config while loading or if user not found
    }

    return {
      breadcrumb: (
        <Breadcrumb
          items={[
            {
              title: (
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/users'); }}>
                  <UserOutlined /> Users
                </a>
              ),
            },
            {
              title: user.email,
            },
          ]}
        />
      ),
      title: <h1 style={{ margin: 0 }}>User Details</h1>,
      extra: (
        <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
          Edit User
        </Button>
      ),
    }
  }, [user, navigate, handleEdit])

  // Set page header in main AppLayout header (must be called unconditionally)
  usePageHeader(pageHeaderConfig)

  // Early returns AFTER all hooks
  if (isLoading) {
    return <PageLoading />
  }

  if (!user) {
    return <div>User not found</div>
  }

  return (
    <div>
      <Card>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Email" span={2}>
            {user.email}
          </Descriptions.Item>
          <Descriptions.Item label="First Name">{user.first_name}</Descriptions.Item>
          <Descriptions.Item label="Last Name">{user.last_name}</Descriptions.Item>
          <Descriptions.Item label="Nickname">
            {user.nickname || <span style={{ color: '#999' }}>Not set</span>}
          </Descriptions.Item>
          <Descriptions.Item label="Receiver">
            <Tag color={user.receiver ? 'green' : 'default'}>{user.receiver ? 'Yes' : 'No'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status" span={2}>
            <Tag color={user.active ? 'success' : 'error'}>
              {user.active ? 'Active' : 'Inactive'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {new Date(user.createdAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {new Date(user.updatedAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="User ID" span={2}>
            <code>{user.id}</code>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <div style={{ marginTop: 16 }}>
        <PermissionManager userId={id!} />
      </div>

      <div style={{ marginTop: 16 }}>
        <UserFeatures userId={id!} />
      </div>

      <div style={{ marginTop: 16 }}>
        <Auth0Integration userId={id!} userEmail={user.email} />
      </div>

      <UserAuditLog userId={id!} />
    </div>
  )
}
